import React, { useEffect, useState } from 'react'

import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

interface Props {
    show: boolean
    // Address stored on the invoice, offered as the default
    defaultEmail?: string
    onClose: () => void
    onConfirm: (email: string) => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/

/**
 * Asks where to send the invoice. The customer's own address is filled in, so the common case is
 * one click, while an invoice can still go to an accountant or a different mailbox.
 */
export const SendInvoiceDialog = ({show, defaultEmail, onClose, onConfirm}: Props) => {
    const {t} = useTranslation()
    const [email, setEmail] = useState(defaultEmail ?? '')
    const [touched, setTouched] = useState(false)

    // The dialog is reused across rows, so it has to follow whichever invoice opened it
    useEffect(() => {
        if (show) {
            setEmail(defaultEmail ?? '')
            setTouched(false)
        }
    }, [show, defaultEmail])

    const isValid = EMAIL_PATTERN.test(email.trim())

    function confirm() {
        if (!isValid) {
            setTouched(true)
            return
        }
        onConfirm(email.trim())
        onClose()
    }

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className={'fs-21'}>{t('send_invoice_title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={'form-group mb-0'}>
                    <label htmlFor={'send-invoice-email'}>{t('email_label')}</label>
                    <input id={'send-invoice-email'}
                           type={'email'}
                           className={'form-control'}
                           value={email}
                           autoFocus
                           onChange={(e) => setEmail(e.target.value)}
                           onBlur={() => setTouched(true)}
                           onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                   confirm()
                               }
                           }}/>
                    {touched && !isValid &&
                        <div className={'text-danger form-text'}>{t('email_invalid')}</div>}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type={'button'} className={'btn amani-secondary'} onClick={onClose}>
                    {t('cancel')}
                </button>
                <button type={'button'} className={'btn amani-primary'} disabled={!isValid}
                        onClick={confirm}>
                    {t('send_to_email')}
                </button>
            </Modal.Footer>
        </Modal>
    )
}
